/** scheduleConflict.ts — 동행상담 · 강의 일정 겹침 검사 (2026-09-28)
 *  상담은 시작 시각만 있다 — 마치는 시각을 짐작하지 않는다(2026-10-18 지도사 「상담 마치는 일정을 등록한 적 없는데 2시간으로 보고 겹친다고 경고」):
 *   같은 시각에 잡힌 상담 · 강의 시간 안에 든 상담만 겹침으로 본다. 강의는 종료 시각이 있으면 그걸, 없으면 2시간.
 *  시간이 비어 있는 일정은 "그날 시간 미정 일정 있음"으로만 알린다(막지 않음).
 */
import type { Consultation } from './types';
import type { Lecture } from './api-lectures';

export const CONSULT_MIN = 1;   // 상담 = 시작 시각 한 점(1분) — 예전 120(2시간 짐작)
export const LECTURE_MIN = 120;

export interface Busy {
  kind: 'consult' | 'lecture';
  id: string;
  date: string;
  start: number | null;   // 분 (null = 시간 미정)
  end: number | null;
  label: string;
  time: string;           // 표시용 "14:00~16:00"
}

export function toMin(t: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
const hhmm = (n: number) => `${String(Math.floor(n / 60) % 24).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
const d10 = (d: string | null | undefined) => (d ? String(d).slice(0, 10) : '');

export function consultBusy(list: Consultation[]): Busy[] {
  const out: Busy[] = [];
  list.forEach(c => {
    const label = `${c.company_name || '업체'}${c.fp_name ? ` · ${c.fp_name}` : ''}`;
    const push = (date: string, time: string, tent: boolean) => {
      if (!date) return;
      const s = toMin(time);
      out.push({ kind: 'consult', id: c.id, date, start: s, end: s == null ? null : s + CONSULT_MIN,
        label: (tent ? '(후보) ' : '') + label, time: s == null ? '시간 미정' : hhmm(s) });
    };
    if (c.confirm_status === 'tentative' && Array.isArray(c.candidate_slots) && c.candidate_slots.length) {
      c.candidate_slots.forEach(sl => push(d10(sl.date), sl.time || '', true));
    } else push(d10(c.date), c.time || '', false);
  });
  return out;
}

export function lectureBusy(list: Lecture[]): Busy[] {
  return list.filter(l => l.lecture_date && l.status !== 'canceled').map(l => {
    const s = toMin(l.start_time);
    const e0 = toMin(l.end_time);
    const e = s == null ? null : (e0 != null && e0 > s ? e0 : s + LECTURE_MIN);
    return { kind: 'lecture' as const, id: l.id, date: l.lecture_date as string, start: s, end: e,
      label: l.title, time: s == null ? '시간 미정' : `${hhmm(s)}~${hhmm(e as number)}` };
  });
}

export interface ConflictResult { overlaps: Busy[]; untimed: Busy[] }

/** date 의 [start,end) 와 겹치는 일정. self 는 제외한다. */
export function findConflicts(all: Busy[], date: string | null, start: string, end: string, defaultMin: number, self?: { kind: Busy['kind']; id?: string }): ConflictResult {
  const res: ConflictResult = { overlaps: [], untimed: [] };
  if (!date) return res;
  const s = toMin(start);
  const e0 = toMin(end);
  const e = s == null ? null : (e0 != null && e0 > s ? e0 : s + defaultMin);
  all.forEach(b => {
    if (b.date !== date) return;
    if (self && self.id && b.kind === self.kind && b.id === self.id) return;
    if (s == null || b.start == null) { res.untimed.push(b); return; }
    if (s < (b.end as number) && b.start < (e as number)) res.overlaps.push(b);
  });
  return res;
}

/** 같은 날 서로 겹치는 일정 id 집합 — 달력 칩에 ⚠ 표시용 */
export function clashIds(all: Busy[]): Set<string> {
  const out = new Set<string>();
  const byDate: Record<string, Busy[]> = {};
  all.forEach(b => { if (b.start != null) (byDate[b.date] = byDate[b.date] || []).push(b); });
  Object.values(byDate).forEach(arr => {
    for (let i = 0; i < arr.length; i++) for (let j = i + 1; j < arr.length; j++) {
      const a = arr[i], b = arr[j];
      if (a.kind === b.kind && a.id === b.id) continue;
      if ((a.start as number) < (b.end as number) && (b.start as number) < (a.end as number)) { out.add(`${a.kind}:${a.id}`); out.add(`${b.kind}:${b.id}`); }
    }
  });
  return out;
}

export function conflictText(r: ConflictResult): string {
  const lines = r.overlaps.map(b => `· ${b.kind === 'lecture' ? '🎤 강의' : '🤝 상담'} ${b.time} ${b.label}`);
  return lines.join('\n');
}
